<template>
  <!-- 商品預訂卡片 -->
  <v-sheet class="booking-product-card pa-5" elevation="0" rounded>
    <div class="product-info-card">
      <v-img
        class="product-cover-image"
        :src="getImage(product.cover_image)"
        max-width="472"
        max-height="262"
        gradient="to bottom, rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.4)"
        cover
      />

      <div class="product-info__content">
        <p class="info__block branch-name mb-0" @click="goToBranch()">
          {{ product.branch_name }}
        </p>

        <p class="info__block product-title mb-0">
          {{ product.product_name }}
        </p>

        <div v-if="!shouldHideComments" class="info__block product-rating d-flex align-center">
          <funnow-icon class="mr-2" max-width="24" :image-path="'star_orange.svg'" />
          <span class="number">
            <span v-if="comments.totalCount === 0"> 5.0 </span>
            <span v-else>
              {{ `${comments.rating || ''} ${commentCount}` }}
            </span>
          </span>
        </div>

        <v-divider class="my-5" />

        <div v-if="showPenaltyRule" class="wd-mb-5">
          <ProductPenaltyHint :penalty-rule="productPenaltyRule" />
        </div>

        <div class="product-booking__block">
          <BookingBanner
            :is-only-one-button-show="isOnlyOneButtonShow"
            :show-price="showPrice"
            :should-hide-rrp="shouldHideRrp"
            :is-book-seat-only="isBookSeatOnly"
            :is-paying-onsite="isPayingOnsite"
            :is-grouping="isGrouping"
            :is-send-gift="showGiftBtn"
            :onboard="onboard"
            :isDisabledByElite="isDisabledByElite"
            :price-setting="{ symbolCode: product.symbol_code, discount: product.discount, rrp: product.rrp, srrp: product.srrp }"
          />
        </div>
      </div>
    </div>
  </v-sheet>
</template>

<script setup lang="ts">
import { isEmpty as _isEmpty } from 'lodash-es'
import { usePlatformStore } from '@/store-pinia/platform'
import FunnowIcon from '@/components/icons/funnow-icon.vue'
import ProductPenaltyHint from '@/components/ProductPenaltyHint.vue'
import utils from '@/utils'
import { ASYNC_DATA_KEYS, getAsyncDataKey } from '@/constants/async-data-keys'
import type { ProductPenaltyRuleResult } from '@/view-models/product'

defineOptions({ name: 'BookingProductCard' })

const props = defineProps({
  product: { type: Object, default: () => ({}) },
  comments: { type: Object, default: () => ({}) },
  onboard: { type: Boolean, default: false },
  isDisabledByElite: { type: Boolean, default: false },
  shouldHideComments: { type: Boolean, default: false },
  isBookSeatOnly: { type: Boolean, default: false },
  isPayingOnsite: { type: Boolean, default: false },
  isOnlyOneButtonShow: { type: Boolean, default: false },
  showPrice: { type: Boolean, default: true },
  shouldHideRrp: { type: Boolean, default: false },
  isGrouping: { type: Boolean, default: false },
  isGift: { type: Boolean, default: false },
  shouldHideGiftButton: { type: Boolean, default: false },
})

const BookingBanner = defineAsyncComponent(() => import('@/components/page/products/booking-banner.vue'))

const nuxtApp = useNuxtApp()
const { isFunBookV2 } = storeToRefs(usePlatformStore())
const router = useRouter()

const showGiftBtn = computed(() => {
  return !props.isGift && !!props.product.giftable && !props.shouldHideGiftButton
})

const commentCount = computed(() => {
  return props.comments.totalCount ? `(${props.comments.totalCount})` : ''
})

const { data: productPenaltyRule } = useNuxtData<ProductPenaltyRuleResult>(getAsyncDataKey(ASYNC_DATA_KEYS.PRODUCT_PENALTY, [props.product?.pid]))

const { hasRule: showPenaltyRule } = useProductPenaltyRule(productPenaltyRule)

function getImage(path: string) {
  return `${utils.CDN}${utils.imagePath(path)}`
}

function goToBranch() {
  const nextHref = isFunBookV2.value
    ? nuxtApp.$localePath(`/funbook/${props.product.bid}/branch`)
    : nuxtApp.$localePath(`/branches/${props.product.bid}`)
  router.push(nextHref)
}
</script>

<style lang="scss" scoped src="@/assets/sass/components/card/booking-product-card.scss"></style>
